import React, { ComponentType, forwardRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

export interface RouterProps {
  location: ReturnType<typeof useLocation>;
  navigate: ReturnType<typeof useNavigate>;
  params: ReturnType<typeof useParams>;
  history: {
    push: (to: string, state?: unknown) => void;
    replace: (to: string | { pathname: string; search?: string; state?: unknown }) => void;
    goBack: () => void;
    location: ReturnType<typeof useLocation>;
  };
  match: {
    params: ReturnType<typeof useParams>;
    path: string;
    url: string;
  };
}

function withRouter<P extends RouterProps>(Component: ComponentType<P>) {
  const displayName = Component.displayName || Component.name || 'Component';

  const ComponentWithRouterProp = forwardRef<unknown, Omit<P, keyof RouterProps>>((props, ref) => {
    const location = useLocation();
    const navigate = useNavigate();
    const params = useParams();

    const history = {
      push: (to: string, state?: unknown) => {
        navigate(to, { state });
      },
      replace: (to: string | { pathname: string; search?: string; state?: unknown }) => {
        if (typeof to === 'string') {
          navigate(to, { replace: true });
        } else {
          const path = to.search ? `${to.pathname}?${to.search}` : to.pathname;
          navigate(path, { replace: true, state: to.state });
        }
      },
      goBack: () => navigate(-1),
      location,
    };

    const match = {
      params,
      path: location.pathname,
      url: location.pathname,
    };

    return (
      <Component
        {...(props as P)}
        ref={ref}
        location={location}
        navigate={navigate}
        params={params}
        history={history}
        match={match}
      />
    );
  });

  ComponentWithRouterProp.displayName = `withRouter(${displayName})`;

  return ComponentWithRouterProp;
}

export default withRouter;
